import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { useData } from "../../context/DataContext";
import {
  cancelCashEntitlement,
  createCashEntitlements,
  getCashEntitlementPayrollSources,
  getCashEntitlements,
} from "../../api/cashEntitlements";
import { mapCashEntitlementRow } from "../../utils/cashEntitlementMapper";
import {
  NO_BRANCH_FILTER,
  NO_DEPARTMENT_FILTER,
  buildCashEntitlementFilterOptions,
  cashEntitlementSourceLabel,
  cashEntitlementStatusMeta,
  cashFilterScopeLabel,
  employeeMatchesCashFilters,
  formatCashAmount,
  formatCashDate,
} from "../../utils/cashEntitlement";
import { isHrFamilyRole } from "../../utils/hrStaffHelpers";
import "../hr/HrOvertimeGrant.css";
import "../advance/AdvanceDisbursementRouting.css";

function employeeLabel(emp) {
  return emp?.name || emp?.fullName || "موظف";
}

function employeeCode(emp) {
  return emp?.code || emp?.employeeCode || "";
}

export default function HrCashEntitlementCreate() {
  const { user } = useAuth();
  const { employeesByBranch } = useData();
  const canCreate = isHrFamilyRole(user?.role) || user?.role === "gm" || user?.role === "admin";

  const allEmployees = useMemo(
    () =>
      Object.values(employeesByBranch || {})
        .flat()
        .filter(
          (e) =>
            e.status !== "terminated" &&
            String(e.id) !== String(user?.employeeId),
        ),
    [employeesByBranch, user?.employeeId],
  );

  const [searchTerm, setSearchTerm] = useState("");
  const [branchFilter, setBranchFilter] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [jobTitleFilter, setJobTitleFilter] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [payrollSources, setPayrollSources] = useState([]);
  const [payrollMeta, setPayrollMeta] = useState({ month: null, year: null });
  const [selectedPayrollKey, setSelectedPayrollKey] = useState("");
  const [sourcesLoading, setSourcesLoading] = useState(false);
  const [draftLines, setDraftLines] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const filterOptions = useMemo(
    () => buildCashEntitlementFilterOptions(allEmployees),
    [allEmployees],
  );

  const filteredEmployees = useMemo(
    () =>
      allEmployees.filter((emp) =>
        employeeMatchesCashFilters(emp, {
          search: searchTerm,
          branch: branchFilter,
          department: departmentFilter,
          jobTitle: jobTitleFilter,
        }),
      ),
    [
      allEmployees,
      searchTerm,
      branchFilter,
      departmentFilter,
      jobTitleFilter,
    ],
  );

  const scopeLabel = cashFilterScopeLabel({
    branch: branchFilter,
    department: departmentFilter,
    jobTitle: jobTitleFilter,
  });

  useEffect(() => {
    if (
      selectedEmployee &&
      !filteredEmployees.some((emp) => emp.id === selectedEmployee.id)
    ) {
      setSelectedEmployee(null);
    }
  }, [filteredEmployees, selectedEmployee]);

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const payload = await getCashEntitlements();
      const list = Array.isArray(payload) ? payload : payload?.items || [];
      setRows(list.map(mapCashEntitlementRow));
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "تعذر تحميل صرف المستحقات",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  useEffect(() => {
    if (!selectedEmployee?.id) {
      setPayrollSources([]);
      setSelectedPayrollKey("");
      return;
    }
    let cancelled = false;
    setSourcesLoading(true);
    getCashEntitlementPayrollSources(selectedEmployee.id)
      .then((payload) => {
        if (cancelled) return;
        setPayrollMeta({
          month: payload?.month ?? null,
          year: payload?.year ?? null,
        });
        setPayrollSources(Array.isArray(payload?.items) ? payload.items : []);
        setSelectedPayrollKey("");
      })
      .catch(() => {
        if (cancelled) return;
        setPayrollSources([]);
      })
      .finally(() => {
        if (!cancelled) setSourcesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedEmployee?.id]);

  const selectedPayroll = payrollSources.find(
    (line) =>
      `${line.payrollRecordId}:${line.payrollFieldKey}` === selectedPayrollKey,
  );

  useEffect(() => {
    if (selectedPayroll) {
      setAmount(String(selectedPayroll.amount));
    }
  }, [selectedPayroll]);

  const addDraftLine = () => {
    setError("");
    setSuccess("");
    if (!canCreate) {
      setError("ليس لديك صلاحية إنشاء صرف مستحقات");
      return;
    }
    if (!selectedEmployee?.id) {
      setError("اختر موظفاً");
      return;
    }
    const value = Number(amount);
    if (!Number.isFinite(value) || value <= 0) {
      setError("أدخل مبلغاً أكبر من صفر");
      return;
    }
    if (!reason.trim()) {
      setError("أدخل سبب الصرف");
      return;
    }

    const line = {
      key: `${selectedEmployee.id}-${Date.now()}`,
      employeeId: selectedEmployee.id,
      employeeName: employeeLabel(selectedEmployee),
      employeeCode: employeeCode(selectedEmployee),
      amount: value,
      reason: reason.trim(),
      source: selectedPayroll ? "PAYROLL" : "MANUAL",
      payrollRecordId: selectedPayroll?.payrollRecordId,
      payrollFieldKey: selectedPayroll?.payrollFieldKey,
      payrollLabel: selectedPayroll
        ? `${selectedPayroll.label} — ${selectedPayroll.month}/${selectedPayroll.year}`
        : null,
    };
    setDraftLines((prev) => [...prev, line]);
    setAmount("");
    setReason("");
    setSelectedPayrollKey("");
  };

  const addFilteredEmployees = () => {
    setError("");
    setSuccess("");
    if (!canCreate) {
      setError("ليس لديك صلاحية إنشاء صرف مستحقات");
      return;
    }
    if (!filteredEmployees.length) {
      setError("لا يوجد موظفون مطابقون للفلاتر");
      return;
    }
    const value = Number(amount);
    if (!Number.isFinite(value) || value <= 0) {
      setError("أدخل مبلغاً أكبر من صفر قبل إضافة المجموعة");
      return;
    }
    if (!reason.trim()) {
      setError("أدخل سبب الصرف قبل إضافة المجموعة");
      return;
    }

    const alreadyIds = new Set(draftLines.map((line) => line.employeeId));
    const toAdd = filteredEmployees.filter((emp) => !alreadyIds.has(emp.id));
    if (!toAdd.length) {
      setError("كل الموظفين الظاهرين مضافون للدفعة بالفعل");
      return;
    }

    const scope = scopeLabel || "الموظفين الظاهرين";
    const ok = window.confirm(
      `إضافة ${toAdd.length} موظف (${scope}) بمبلغ ${formatCashAmount(value)} للدفعة؟`,
    );
    if (!ok) return;

    const stamp = Date.now();
    const lines = toAdd.map((emp, index) => ({
      key: `${emp.id}-${stamp}-${index}`,
      employeeId: emp.id,
      employeeName: employeeLabel(emp),
      employeeCode: employeeCode(emp),
      amount: value,
      reason: reason.trim(),
      source: "MANUAL",
    }));
    setDraftLines((prev) => [...prev, ...lines]);
    setSuccess(`تمت إضافة ${toAdd.length} موظف للدفعة`);
  };

  const removeDraftLine = (key) => {
    setDraftLines((prev) => prev.filter((line) => line.key !== key));
  };

  const handleSubmit = async () => {
    if (!draftLines.length || submitting) return;
    setSubmitting(true);
    setError("");
    setSuccess("");
    try {
      await createCashEntitlements({
        items: draftLines.map((line) => ({
          employeeId: line.employeeId,
          amount: line.amount,
          reason: line.reason,
          source: line.source,
          payrollRecordId: line.payrollRecordId,
          payrollFieldKey: line.payrollFieldKey,
        })),
      });
      setDraftLines([]);
      setSuccess("تم إرسال صرف المستحقات للمحاسب العام");
      await loadRows();
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "تعذر إنشاء صرف المستحقات",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async (row) => {
    if (!row?.id || row.cancelledAt || row.disbursementStatus === "DISBURSED") {
      return;
    }
    const ok = window.confirm(
      `إلغاء صرف مستحقات ${row.employeeName || "الموظف"} بمبلغ ${formatCashAmount(row.amount)}؟`,
    );
    if (!ok) return;
    try {
      await cancelCashEntitlement(row.id);
      await loadRows();
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "تعذر إلغاء الصرف",
      );
    }
  };

  return (
    <div className="hr-overtime-grant" dir="rtl">
      <h1 className="ess-home-title" style={{ marginBottom: 8 }}>
        صرف مستحقات نقدية
      </h1>
      <p className="text-muted" style={{ marginBottom: 16 }}>
        أنشئ صرفاً نقدياً لموظف أو لمجموعة حسب الفرع أو الإدارة أو المسمى
        الوظيفي، ثم أرسله للمحاسب العام ليوجّهه لمسؤول الخزنة أو كاشيرية الفرع.
        الربط ببند البي رول للمرجع فقط — المبلغ لن يُخصم من المرتب تلقائياً،
        ولو البند ما زال في شيت المرتب لازم يتشال يدوي عشان الموظف ما يتدفعش
        مرتين.
      </p>

      {error ? (
        <div className="adv-disburse-error-banner" style={{ marginBottom: 14 }}>
          {error}
        </div>
      ) : null}
      {success ? (
        <p className="text-success" style={{ marginBottom: 14 }}>
          {success}
        </p>
      ) : null}

      <div className="hr-overtime-grant__grid">
        <section className="hr-overtime-grant__panel">
          <h2>اختيار الموظف أو المجموعة</h2>
          <div className="cash-entitlement-filters">
            <select
              className="form-control"
              value={branchFilter}
              onChange={(ev) => setBranchFilter(ev.target.value)}
              aria-label="فلتر الفرع"
            >
              <option value="">كل الفروع</option>
              {filterOptions.branches.map((name) => (
                <option key={name} value={name}>
                  {name === NO_BRANCH_FILTER ? "بدون فرع (إدارة)" : name}
                </option>
              ))}
            </select>
            <select
              className="form-control"
              value={departmentFilter}
              onChange={(ev) => setDepartmentFilter(ev.target.value)}
              aria-label="فلتر الإدارة"
            >
              <option value="">كل الإدارات</option>
              {filterOptions.departments.map((name) => (
                <option key={name} value={name}>
                  {name === NO_DEPARTMENT_FILTER ? "بدون إدارة" : name}
                </option>
              ))}
            </select>
            <select
              className="form-control"
              value={jobTitleFilter}
              onChange={(ev) => setJobTitleFilter(ev.target.value)}
              aria-label="فلتر المسمى الوظيفي"
            >
              <option value="">كل المسميات الوظيفية</option>
              {filterOptions.jobTitles.map((title) => (
                <option key={title} value={title}>
                  {title}
                </option>
              ))}
            </select>
          </div>
          <input
            type="search"
            className="form-control mb-2"
            placeholder="بحث بالاسم أو الكود..."
            value={searchTerm}
            onChange={(ev) => setSearchTerm(ev.target.value)}
          />
          <p className="cash-entitlement-filter-count">
            ظاهر {filteredEmployees.length} موظف
            {scopeLabel ? ` — ${scopeLabel}` : ""}
          </p>
          <div className="hr-overtime-grant__list">
            {filteredEmployees.length === 0 ? (
              <p className="text-muted">لا يوجد موظفون مطابقون للفلاتر</p>
            ) : (
              <>
                {filteredEmployees.slice(0, 80).map((emp) => (
                  <button
                    key={emp.id}
                    type="button"
                    className={`hr-overtime-grant__emp ${
                      selectedEmployee?.id === emp.id ? "is-selected" : ""
                    }`}
                    onClick={() => setSelectedEmployee(emp)}
                  >
                    <strong>{employeeLabel(emp)}</strong>
                    <span>
                      {employeeCode(emp)}
                      {emp.jobTitle ? ` · ${emp.jobTitle}` : ""}
                    </span>
                  </button>
                ))}
                {filteredEmployees.length > 80 ? (
                  <p className="text-muted">
                    عرض أول 80 من {filteredEmployees.length} — «إضافة الظاهر
                    للدفعة» يضيف الجميع.
                  </p>
                ) : null}
              </>
            )}
          </div>
        </section>

        <section className="hr-overtime-grant__panel">
          <h2>تفاصيل الصرف</h2>
          <p className="hr-overtime-grant__form">
            <span className="form-control-plaintext">
              {selectedEmployee
                ? `${employeeLabel(selectedEmployee)} (${employeeCode(selectedEmployee)})`
                : scopeLabel
                  ? `مجموعة: ${scopeLabel} (${filteredEmployees.length} موظف)`
                  : "لم يُختار موظف — أو فلتر فرع / إدارة / مسمى لإرسال مجموعة"}
            </span>
          </p>

          {sourcesLoading ? (
            <p className="text-muted">جاري تحميل بنود البي رول...</p>
          ) : payrollSources.length > 0 ? (
            <label className="d-block mb-2">
              بند من البي رول (اختياري)
              {payrollMeta.month && payrollMeta.year
                ? ` — ${payrollMeta.month}/${payrollMeta.year}`
                : ""}
              <select
                className="form-control"
                value={selectedPayrollKey}
                onChange={(ev) => setSelectedPayrollKey(ev.target.value)}
              >
                <option value="">صرف يدوي — بدون ربط</option>
                {payrollSources.map((line) => (
                  <option
                    key={`${line.payrollRecordId}:${line.payrollFieldKey}`}
                    value={`${line.payrollRecordId}:${line.payrollFieldKey}`}
                  >
                    {line.label} — {formatCashAmount(line.amount)}
                  </option>
                ))}
              </select>
            </label>
          ) : selectedEmployee ? (
            <p className="text-muted">
              لا توجد بنود نقدية متاحة في آخر مرتب لهذا الموظف.
            </p>
          ) : null}

          <label className="d-block mb-2">
            المبلغ
            <input
              type="number"
              min="0.01"
              step="0.01"
              className="form-control"
              value={amount}
              onChange={(ev) => setAmount(ev.target.value)}
            />
          </label>
          <label className="d-block mb-2">
            سبب الصرف
            <input
              type="text"
              className="form-control"
              value={reason}
              onChange={(ev) => setReason(ev.target.value)}
              maxLength={500}
              placeholder="حافز، مكافأة، باقي مستحقات..."
            />
          </label>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={addDraftLine}
            disabled={!canCreate}
          >
            إضافة الموظف المختار
          </button>
          <button
            type="button"
            className="btn btn-primary"
            style={{ marginRight: 8 }}
            onClick={addFilteredEmployees}
            disabled={!canCreate || filteredEmployees.length === 0}
          >
            إضافة الظاهر للدفعة
            {filteredEmployees.length ? ` (${filteredEmployees.length})` : ""}
          </button>
        </section>
      </div>

      <section className="hr-overtime-grant__panel" style={{ marginTop: 16 }}>
        <h2>الدفعة قبل الإرسال ({draftLines.length})</h2>
        {draftLines.length === 0 ? (
          <p className="text-muted">أضف موظفاً أو أكثر ثم أرسل للمحاسب العام.</p>
        ) : (
          <>
            <div className="adv-disburse-table-wrap">
              <table className="adv-disburse-table">
                <thead>
                  <tr>
                    <th>الموظف</th>
                    <th>المبلغ</th>
                    <th>المصدر</th>
                    <th>السبب</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {draftLines.map((line) => (
                    <tr key={line.key}>
                      <td>
                        {line.employeeName} ({line.employeeCode || "—"})
                      </td>
                      <td>{formatCashAmount(line.amount)}</td>
                      <td>
                        {line.payrollLabel ||
                          cashEntitlementSourceLabel(line.source)}
                      </td>
                      <td>{line.reason}</td>
                      <td>
                        <button
                          type="button"
                          className="adv-disburse-btn compact danger"
                          onClick={() => removeDraftLine(line.key)}
                        >
                          حذف
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button
              type="button"
              className="btn btn-primary"
              style={{ marginTop: 12 }}
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? "جاري الإرسال..." : "إرسال للمحاسب العام"}
            </button>
          </>
        )}
      </section>

      <section className="hr-overtime-grant__panel" style={{ marginTop: 16 }}>
        <h2>آخر عمليات الإنشاء</h2>
        {loading ? (
          <p className="text-muted">جاري التحميل...</p>
        ) : rows.length === 0 ? (
          <p className="text-muted">لا توجد مستحقات نقدية بعد.</p>
        ) : (
          <div className="adv-disburse-table-wrap">
            <table className="adv-disburse-table">
              <thead>
                <tr>
                  <th>الموظف</th>
                  <th>المبلغ</th>
                  <th>المصدر</th>
                  <th>الحالة</th>
                  <th>التاريخ</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.slice(0, 40).map((row) => {
                  const status = cashEntitlementStatusMeta(row);
                  const canCancel =
                    !row.cancelledAt &&
                    row.disbursementStatus === "PENDING_ASSIGNMENT";
                  return (
                    <tr key={row.id}>
                      <td>
                        {row.employeeName} ({row.employeeCode || "—"})
                      </td>
                      <td>{formatCashAmount(row.amount)}</td>
                      <td>{cashEntitlementSourceLabel(row.source)}</td>
                      <td>
                        <span className={`adv-disburse-badge ${status.className}`}>
                          {status.label}
                        </span>
                      </td>
                      <td>{formatCashDate(row.createdAt)}</td>
                      <td>
                        {canCancel ? (
                          <button
                            type="button"
                            className="adv-disburse-btn compact danger"
                            onClick={() => handleCancel(row)}
                          >
                            إلغاء
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
